import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import FilterApi from "@/api/FilterApi";
import { selectedFiltersSelector } from "@/components/filter/selectedFiltersSelector";
import { storesSelectState } from "@/components/filter/storesSelectState";
import { useFilterHandlers } from "@/components/filter/useFilterHandlers";
import { ArrowToggle } from "@/components/ui-components/ArrowToggler";
import SelectOption from "@/components/ui-components/SelectOption/SelectOption";
import { ymReachGoalHelper } from "@/components/YandexMetrica";
import { getStoresByCity } from "@/helpers/getStoresByCity";
import { useClickOutside } from "@/hooks/useClickOutside";

import styles from "./CitySelect.module.scss";

const CITIES = ["Москва", "Санкт-Петербург", "Казань", "Интернет-магазин"];

interface CitySelectProps {
  defaultCitySelected?: string;
  labelClassName?: string;
  optionsClassName?: string;
}

const CitySelect = ({
  defaultCitySelected,
  labelClassName,
  optionsClassName,
}: CitySelectProps) => {
  const [selectedCity, setSelectedCity] = useState(
    defaultCitySelected || localStorage.getItem("city")
  );
  const [opened, setOpened] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selectedFilters = useRecoilValue(selectedFiltersSelector);
  const setStoresSelect = useSetRecoilState(storesSelectState);

  const filterQuery = useQuery(
    ["filterQuery", selectedFilters],
    () => FilterApi.filter(selectedFilters),
    {
      keepPreviousData: true,
    }
  );
  const stores = filterQuery?.data?.data.stores || [];

  const toggle = () => setOpened((state) => !state);
  const close = () => setOpened(false);

  useClickOutside(rootRef, close);
  const { applyStores } = useFilterHandlers();

  const selectStores = (city: string) => {
    const selectedStoreIds = getStoresByCity(stores, city).map(
      (store) => store.id
    );
    applyStores(selectedStoreIds);
    setStoresSelect(selectedStoreIds);
  };

  const selectCity = (city: string) => {
    setSelectedCity(city);
    localStorage.setItem("city", city);
    close();
    selectStores(city);
    ymReachGoalHelper("filter-choose-city");
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <div
        className={classNames(styles.label, labelClassName)}
        onClick={toggle}
      >
        <span>{selectedCity || "Город"}</span> <ArrowToggle opened={opened} />
      </div>
      <div
        className={classNames(styles.options, optionsClassName, {
          [styles.opened]: opened,
        })}
      >
        {CITIES.map((city) => (
          <SelectOption
            text={city}
            key={city}
            select={selectCity}
            selected={city === selectedCity}
          />
        ))}
      </div>
    </div>
  );
};

export default CitySelect;
